import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Stack, Typography } from "@mui/material";
import { Form, Formik } from 'formik';
import {
  Requisition,
  RequisitionData,
  RequisitionItem,
  useGetDevelopmentsByIdQuery,
  useGetProcurementProcessesByIdQuery,
  useGetUsersMeQuery,
  usePostCreateProcurementProcessMutation,
  usePutProcurementProcessesByIdMutation,
} from '../../../redux/vmsApi';
import LoadingCover from '../../common/LoadingCover';
import PageWithHeader from '../../common/PageWithHeader';
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from 'date-fns/locale';
import { toast } from 'react-toastify';

import createRequisitionModel, { createRequisitionModelType } from "../../forms/models/createRequisitionModel";
import CreateRequisitionForm from "../../forms/CreateRequisitionForm";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ProcurementProcessDashboard from '../components/ProcurementProcessDashboard';
import RenderRequisitionDetails from '../common/RenderRequisitionDetails';
import RenderRequisitionHeader from '../common/renderRequisitionHeader';


type MyFormValues = createRequisitionModelType;

const createRequisitionModelInitial = createRequisitionModel.initialValues;

const CreateRequisitionPage: React.FC<{ onSuccess?: (requisition: Requisition) => void; }> = () => {
  const [isClicked, setIsClicked] = useState(false);
  const isInProcess: boolean = true;
  const params = useParams();
  const [updateProcurementProcess, { isLoading: isSubmitting }] = usePutProcurementProcessesByIdMutation();
  const { data: user } = useGetUsersMeQuery();
  const [createProcurementProcessForm, { isLoading: isSecondFormLoading }] = usePostCreateProcurementProcessMutation();
  const [showOverlay, setShowOverlay] = useState(false);
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);
  const [redirectToDashboard, setRedirectToDashboard] = useState(false);
  const [submitGuardMessage, setSubmitGuardMessage] = useState('');


  const { data: procurementProcess } = useGetProcurementProcessesByIdQuery({ id: params?.uuid ?? "" }, { skip: !params?.uuid });
  const developmentId = procurementProcess?.requisition?.development.id;
  const { data: development } = useGetDevelopmentsByIdQuery(
    { id: developmentId! },
    { skip: !developmentId }
  );
  useEffect(() => {
    setShowOverlay(isSubmitting || isSecondFormLoading);
    if (showSuccessMsg) {
      setTimeout(() => {
        setShowOverlay(false);

        setRedirectToDashboard(true);
      }, 3000);
    }
  }, [isSubmitting, isSecondFormLoading, showSuccessMsg]);

  const initialValues: MyFormValues = {
    ...createRequisitionModelInitial,
    priority: (createRequisitionModelInitial.priority as MyFormValues['priority']) || 'Standard',
  };

  // const generateRequisitionNumber = (date = new Date()) => {
  //   const year = date.getFullYear();
  //   const month = String(date.getMonth() + 1).padStart(2, '0');
  //   const day = String(date.getDate()).padStart(2, '0');
  //   const hours = String(date.getHours()).padStart(2, '0');
  //   const minutes = String(date.getMinutes()).padStart(2, '0');
  //   const seconds = String(date.getSeconds()).padStart(2, '0');


  //   return `REQ-${year}${month}${day}-${hours}${minutes}${seconds}`;
  // };

  const handleSubmit = async (values: MyFormValues) => {
    const isUpdatingExisting = Boolean(procurementProcess?.requisition);
    const blocked = Boolean((values as any)?.bomSubmissionBlocked);
    const blockMessage = String((values as any)?.bomSubmissionMessage ?? '');
    if (blocked) {
      setSubmitGuardMessage(blockMessage || 'Cannot raise requisition until BOM constraints are satisfied.');
      return;
    }

    setSubmitGuardMessage('');
    setIsClicked(true);
    const requisitionItems: RequisitionItem[] = values.items.map((item) => ({
      description: item.description,
      itemCode: item.itemCode || '',
      quantity: {
        quantityValue: typeof item.quantity.quantityValue === 'string'
          ? parseFloat(item.quantity.quantityValue.replace(/,/g, '')) || 0
          : item.quantity.quantityValue,
        quantityUnit: item.quantity.quantityUnit,
      },
      unit: {
        id: item.unit,
        name: item.unitName
      },
      rate: typeof item.rate === 'string'
        ? parseFloat((item.rate as string).replace(/,/g, '')) || 0
        : item.rate === null
          ? 0
          : item.rate,
      procured: false,
      totalCostPerItem: typeof item.totalCostPerItem === 'string'
        ? parseFloat((item.totalCostPerItem as string).replace(/,/g, '')) || 0
        : item.totalCostPerItem === null
          ? 0
          : item.totalCostPerItem || 0
    }));

    const reqData: RequisitionData = {
      title: values.title,
      development: {
        name: values.developmentName,
        id: values.development
      },
      project: {
        name: values.projectName,
        id: values.project
      },
      items: requisitionItems,
      totalCost: values.totalCost,
      approved: false,
      reqNumber: ``,
      documents: values.documents
    };

    setShowOverlay(true);

    try {
      let result;
      let procurementProcessPartial: any;
      let newProcurementProcess: any;
      let currentDate = new Date();
      if (procurementProcess?.requisition) {
        const documents = Array.from(
          new Map(
            (values.documents || []).map((doc: any) => [
              `${String(doc?.name ?? '')}::${String(doc?.url ?? '')}`,
              doc,
            ])
          ).values()
        );

        const selectedDevelopment = {
          id: reqData.development.id || procurementProcess?.requisition?.development?.id,
          name: reqData.development.name || procurementProcess?.requisition?.development?.name,
        };
        const selectedProject = {
          id: reqData.project.id || procurementProcess?.requisition?.project?.id,
          name: reqData.project.name || procurementProcess?.requisition?.project?.name,
        };

        const resolvedRequestType = values.requestType || procurementProcess?.requestType || 'PROCUREMENT';

        procurementProcessPartial = {
          currentTask: "ProjectManagerReviewPurchaseRequisition",
          previousTask: "RaisePurchaseRequisition",
          currentTaskOwner: "project-manager",
          priority: values.priority || procurementProcess?.priority || 'Standard',
          urgentMarkedBy: procurementProcess?.urgentMarkedBy,
          urgentMarkedAt: procurementProcess?.urgentMarkedAt,
          initiatedDate: procurementProcess?.initiatedDate,
          lastActionedDate: currentDate,
          status: "New Requisition",
          requestType: resolvedRequestType,
          siteManagerReview: {
            comment: procurementProcess?.siteManagerReview?.comment,
            approved: procurementProcess?.siteManagerReview?.approved,
            dateReviewed: procurementProcess?.siteManagerReview?.dateReviewed,
            user: {
              ...procurementProcess?.siteManagerReview?.user,
              previousToken: '',
            },
            quotations:[]
          },
          projectManagerReview: procurementProcess?.projectManagerReview
            ? {
              comment: procurementProcess.projectManagerReview.comment,
              approved: procurementProcess.projectManagerReview.approved,
              dateReviewed: procurementProcess.projectManagerReview.dateReviewed,
              user: {
                ...procurementProcess.projectManagerReview.user,
                previousToken: '',
              },
              quotations:[]
            }
            : undefined,

          pcReview: procurementProcess?.pcReview
            ? {
              comment: procurementProcess.pcReview.comment,
              approved: procurementProcess.pcReview.approved,
              dateReviewed: procurementProcess.pcReview.dateReviewed,
              user: {
                ...procurementProcess.pcReview.user,
                previousToken: '',
              },
              quotations: procurementProcess?.pcReview?.quotations?.map((quotation) => ({
                paymentTerms: quotation.paymentTerms,
                remarks: quotation.remarks,
                supplier: quotation.supplier,
                currency: quotation.currency,
                quotationNumber: quotation.quotationNumber,
                deliveryLeadTime: quotation.deliveryLeadTime,
                paymentUpfrontPercentage: (quotation.paymentUpfrontPercentage as unknown) === '' || quotation.paymentUpfrontPercentage == null ? undefined : Math.trunc(Number(quotation.paymentUpfrontPercentage)),
                termsAndConditionsTemplate: (quotation as any).termsAndConditionsTemplate,
                termsAndConditions: (quotation as any).termsAndConditions,
                attachments: quotation.attachments?.map((attachment) => ({
                  name: attachment.name,
                  url: attachment.url,
                })),
                  quotationItems: quotation.quotationItems?.map((quotationItem, itemIndex) => ({
                    ...quotationItem,
                    itemCode:
                      quotationItem?.itemCode
                      || procurementProcess?.pcReview?.quotations?.find((q) => q.supplier?.id === quotation.supplier?.id)?.quotationItems?.[itemIndex]?.itemCode
                      || '',
                  })),
                selected:quotation.selected
              })),
            }
            : undefined,

          cpoReview: procurementProcess?.cpoReview
            ? {
              comment: procurementProcess.cpoReview.comment,
              approved: procurementProcess.cpoReview.approved,
              dateReviewed: procurementProcess.cpoReview.dateReviewed,
              user: {
                ...procurementProcess.cpoReview.user,
                previousToken: '',
              },
              quotations:[]
            }
            : undefined,

          proChairReview: procurementProcess?.proChairReview
            ? {
              comment: procurementProcess.proChairReview.comment,
              approved: procurementProcess.proChairReview.approved,
              dateReviewed: procurementProcess.proChairReview.dateReviewed,
              user: {
                ...procurementProcess.proChairReview.user,
                previousToken: '',
              },
              quotations:[]
            }
            : undefined,
          initiatedByUser: {
            ...procurementProcess?.initiatedByUser,
            previousToken: '',
          },
          requisition: {
            ...procurementProcess?.requisition,
            id: '',
            title: values.title,
            project: selectedProject,
            development: selectedDevelopment,
            documents: documents,
            reqNumber: procurementProcess?.requisition.reqNumber,
            totalCost: values.totalCost,
            items: reqData.items?.map((item: any) => ({
              ...item,
              itemCode: item.itemCode || '',
              quantity: {
                ...item.quantity,
                id: undefined,
              },
              unit: {
                id: item.unit.id,
                name: item.unit.name
              },
              id: undefined,
            })),
          },
          requisitionDocument: procurementProcess?.requisitionDocument,
          purchaseOrderDocument: procurementProcess?.purchaseOrderDocument,
        };
        console.log('procurementProcessPartial', procurementProcessPartial);
        result = await updateProcurementProcess({ id: params.uuid ?? "", procurementProcessPartial }).unwrap();
        toast.success('Procurement process updated successfully.');
      } else {
        // reqData["reqNumber"] = generateRequisitionNumber(currentDate);
        const resolvedRequestType = values.requestType || 'PROCUREMENT';

        newProcurementProcess = {
          currentTask: "ProjectManagerReviewPurchaseRequisition",
          previousTask: "RaisePurchaseRequisition",
          currentTaskOwner: "project-manager",
          priority: values.priority || 'Standard',
          initiatedDate: currentDate,
          lastActionedDate: currentDate,
          requestType: resolvedRequestType,
          initiatedByUser: user,
          requisition: reqData,
          siteManagerReview: undefined,
          status: "New Requisition",
          requisitionDocument: { name: "", url: "" },
          purchaseOrderDocument: { name: "", url: "" },
        };

        result = await createProcurementProcessForm({ newProcurementProcess }).unwrap();
      }

      if (result) {
        setShowOverlay(false);
        setShowSuccessMsg(true);
      }
    } catch (error) {
      if (isUpdatingExisting) {
        toast.error('Failed to update procurement process.');
      }
      setShowOverlay(false);
      console.error("Error submitting ProcurementProcessForm:", error);
    }
  };

  if (redirectToDashboard) {
    return <ProcurementProcessDashboard />;
  }

  return (
    <PageWithHeader>
      {showOverlay && <LoadingCover isSuccess={showSuccessMsg} />}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
            <Form>
              <Stack spacing={4} width="100%" p={10} pb={5}>
                {procurementProcess && (
                  <>
                    <RenderRequisitionHeader development={development || 'Development'} />
                    <RenderRequisitionDetails
                      development={development}
                      procurementProcess={procurementProcess}
                      selectedSupplier={null}
                      openDialog={null}
                      handleDialogOpen={() => { }}
                      handleDialogClose={() => { }}
                      handleSelectSupplier={() => { }}
                      showQuotationForm={false}
                      setShowQuotationForm={() => { }}
                      isInProcess={isInProcess}
                    />
                  </>
                )}

                <CreateRequisitionForm />

                {(Boolean((formik.values as any)?.bomSubmissionBlocked) || Boolean(submitGuardMessage)) && (
                  <Typography sx={{ color: '#b42318', fontWeight: 600 }}>
                    {String((formik.values as any)?.bomSubmissionMessage || submitGuardMessage)}
                  </Typography>
                )}

                <Button
                  variant="contained"
                  type="submit"
                  disabled={isClicked || Boolean((formik.values as any)?.bomSubmissionBlocked)}

                >
                  {procurementProcess?.requisition ? 'Resubmit Corrected Requisition' : 'Raise Requisition'}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setRedirectToDashboard(true)}
                >
                  Go to Dashboard
                </Button>
              </Stack>
            </Form>
          </LocalizationProvider>
        )}
      </Formik>
    </PageWithHeader>
  );
};

export default CreateRequisitionPage;